/**
 * Attribute normalisation to OTLP AnyValue.
 */

import type { AttributeValue, Attributes, OtlpAnyValue, OtlpKeyValue } from './types';

/** Attribute values longer than this are truncated, with a marker suffix. */
const MAX_STRING_LENGTH = 4096;

function truncate(value: string): string {
	if (value.length <= MAX_STRING_LENGTH) return value;
	return `${value.slice(0, MAX_STRING_LENGTH)}…[truncated ${value.length - MAX_STRING_LENGTH}]`;
}

function toAnyValue(value: Exclude<AttributeValue, null | undefined>): OtlpAnyValue {
	if (typeof value === 'string') return { stringValue: truncate(value) };
	if (typeof value === 'boolean') return { boolValue: value };
	if (typeof value === 'bigint') return { intValue: value.toString() };
	if (typeof value === 'number') {
		// OTLP distinguishes int from double, and encodes int as a *string*.
		// Non-finite numbers have no OTLP representation, so they degrade to a
		// string rather than emitting invalid JSON (NaN/Infinity are not valid).
		if (!Number.isFinite(value)) return { stringValue: String(value) };
		return Number.isInteger(value) ? { intValue: value.toString() } : { doubleValue: value };
	}
	// Array — OTLP arrays must be homogeneous in practice; we normalise each
	// element independently which is what the ClickHouse exporter expects.
	return {
		arrayValue: {
			values: value.map((v) => toAnyValue(v)),
		},
	};
}

/**
 * Convert a loose attribute record into OTLP key/values.
 * null/undefined entries are dropped rather than emitted as empty strings, so
 * optional attributes simply do not appear.
 */
export function attrs(input: Attributes | undefined): OtlpKeyValue[] | undefined {
	if (!input) return undefined;
	const out: OtlpKeyValue[] = [];
	for (const key in input) {
		const value = input[key];
		if (value === null || value === undefined) continue;
		out.push({ key, value: toAnyValue(value) });
	}
	return out.length > 0 ? out : undefined;
}
